import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg';
import Button from '@/components/Button/index';
import DexLeftComponent from '@/components/DexLeftComponent';
import IconEye from '@/components/IconEye';
import Input from '@/components/Input/index';
import Toast2 from '@/components/Toast2';
import googleKeys from '@/constants/configuration/google-keys.json';
import useAuthLogin from '@/hooks/use-auth-login';
import usePage from '@/hooks/use-page';
import useStepList from '@/hooks/use-step-list';
import { useLocation, useNavigate } from '@/lib/router-dom';
import { login } from '@/redux/actions/auth.js';
import api from '@/services/api/config.js';
import API_LOGIN from '@/services/api/login';
import "@/translations/i18n";
import { getDexConfig } from '@/utils/get-dex-config';
import { getEnvAndDex } from "@/utils/get-env-and-dex";
import removeUrlTrailingSlash from '@/utils/remove-url-trailing-slash';
import { validateDomain } from '@/utils/validate-domain.js';
import { validateEmail } from '@/utils/validate-email.js';
import classname from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import TencentCaptcha from '@/components/TencentCaptcha';
import tencentKeys from '@/constants/configuration/tencent-keys.json';

const { env, dex } = getEnvAndDex();

const Login = () => {
	usePage({ title: 'Login' })

	const { goTo, replace } = useNavigate();
	const {currentPath, currentState} = useLocation()
	const dispatch = useDispatch()
	const loggedIn = useSelector(state => state.loggedIn)
	const [loading, setLoading] = useState(false)
	const [errorMessage, setErrorMessage] = useState()
	const [pitstopConfigured, setPitstopConfigured] = useState(false);
	const [customMask, setCustomMask] = useState(true);
	const [passwordField, setPasswordField] = useState(false)
	const redirectURL = encodeURI(window.location.origin + `/auth/redirect`);
	const { getAuthorizationURL } = useAuthLogin();
	const [orgData, setOrgData] = useState({});
	const [showLoginPage, setShowLoginPage] = useState(false);

	const tfa = useSelector(state => state.auth.tfa)
	const user = useSelector(state => state.auth.user)
	const captchaRef = useRef(null)

	const defaultCaptchaValue = !!googleKeys[dex]?.[env]?.CAPTCHA_SITE_KEY;
	const [captchaToken, setCaptchaToken] = useState("")

	const tencentCaptchaAppId = tencentKeys[dex]?.[env]?.CAPTCHA_APP_ID;
	const [tencentCaptchaToken, setTencentCaptchaToken] = useState("")
	const [isTencentCaptchaEnabled, setIsTencentCaptchaEnabled] = useState(false);
	const useTencentCaptcha = isTencentCaptchaEnabled && !!tencentCaptchaAppId

	if (loggedIn) {
		goTo('/shared-data')
	}

	async function handleChangeReCaptcha(value) {
		setLoading(true)
		const [error] =  await API_LOGIN.siteVerify({token: value})
		setLoading(false)
		if (error) {
			setCaptchaToken(false)
			if (captchaRef.current) {
				captchaRef.current.reset();
			}
			return setErrorMessage("reCAPTCHA verification failed")
		}
		setCaptchaToken(true)
	}

	const handleTencentReCaptchaChange = useCallback(
		async (ticket, randstr) => {
			setLoading(true)

			const [error] = await API_LOGIN.siteVerify({ ticket, randstr });
			setLoading(false)

			if (error) {
				setTencentCaptchaToken(false)
				return setErrorMessage("reCAPTCHA verification failed")
			}

			setTencentCaptchaToken(true)
		},
		[]
	)

	useEffect(() => {
		const body = {
			redirectUrl: redirectURL
		}
		getAuthorizationURL(body).then((data) => {
			setOrgData(data);
			if (localStorage.getItem("userEmail")) {
				window.open(data?.sso.authUrl, "_self")
			}
			else {
				setShowLoginPage(true)
			}
		})
		.catch((e) => {
				if (e.message) {
					setErrorMessage(e.message)
				}
				else {
					setErrorMessage("Something went wrong! Please try again.")
				}
			})
		if (currentState) {
			let message;

			if (currentState?.message?.includes("Failed to validate auth")) {
				message = "There was a problem with the SSO login. Please contact the administrator."
			}
			else if (currentState?.message === "credentialError") {
				message = "We are unable to log you in. Please contact the administrator."
			}
			else {
				message = currentState?.state?.message
			}

			setErrorMessage(message)
			replace(currentPath, {});
		}
	}, [])

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	function handleError() {
		setError()
	}

	const version = useSelector((state) => state.version.data)

	const { register, handleSubmit, formState: { errors, isValid }, setValue, getValues } = useForm({
		mode: 'onChange'
	})

	const [adminUrl, setAdminUrl] = useState()
	const [passwordFieldState, setPasswordFieldState] = useState(false)
	const [changeButtonTextForLockedUsers, setChangeButtonTextForLockedUsers] = useState(false)

	async function fetchStatus() {
		const [error, data] = await api.checkStatus()

		if (error) {
			setError("Invalid pitstop URL. Please ensure that the URL is correct or contact the Administrator for assistance.")
		}

		const url = removeUrlTrailingSlash(data?.adminBaseUrl)
		setAdminUrl(url)

		const tencentFlag = data?.tencentRecaptchaEnabled;
		setIsTencentCaptchaEnabled(tencentFlag === true || tencentFlag === 'true');

		return data?.pitstopConfigured
	}

	useEffect(() => {
		fetchStatus().then(value => {
			if (value === false) {
				goTo(`/installation`)
			} else {
				setPitstopConfigured(true);
			}
		})
	}, [])

	useEffect(() => {
		if(tfa){
			nextStep()
		}
	}, [tfa])

	const passwordInputClass = classname({
		'input--error': errors.password,
		'login-box__password': true
	})


	function resetValue() {
		setValue('password', "");
		setValue('secret', "");
		setPasswordFieldState(false)
	}

	async function loginHandler(data) {

		if (changeButtonTextForLockedUsers) {
			return window.location.reload()
		}

		if (loading) {
			return
		}

		setErrorMessage(null)
		setLoading(true)

		try {
			if (!passwordField) {
				localStorage.setItem("userEmail", data.email)
				window.open(orgData.sso.authUrl, "_self")
			}
			else {
				await dispatch(login(goTo, data, "login"))
			}
		} catch (e) {
			if (e.message === "credentialError") {
				setErrorMessage("Incorrect password or email address.")
			}
			else if (e.message.includes("Your account has been locked")) {
				setErrorMessage("Your account has been locked. We sent a link to reset your password to the provided email account.")
				return setChangeButtonTextForLockedUsers(true)
			}
			else {
				setErrorMessage(e.message)
			}
			captchaRef.current?.reset();
			resetValue()
		}
		setLoading(false)
	}

	async function otpHandler(data) {
		if (loading) {
			return
		}

		setErrorMessage(null)
		setLoading(true)
		try {
			const dataWithSecret = {
				otp: data.otp
			}
			await dispatch(login(goTo, dataWithSecret, "otpVerify"))
		} catch (e) {
			setErrorMessage(e.message)
			resetValue()
		}

		setLoading(false)
	}

	function goToForgetPassword() {
		return window.open(`${adminUrl}/forget-password?email=${getValues("email")}`, '_blank')
	}

	function goToResetTfa() {
		return window.open(`${adminUrl}/reset-2fa?email=${user?.email}`, '_blank')
	}

	function handleChange(input){
		if(!validateEmail(input)){
			setPasswordField(false)
			return false
		}

		if (validateDomain(input, orgData)) {
			setPasswordField(false)
			return true
		}
		setPasswordField(true)
		return true
	}

	function handlePasswordChange(input) {
		if (input) {
			setPasswordFieldState(true)
		}
	}

	window.recaptchaOptions = {
		useRecaptchaNet: true,
	};

	/*=================================================
		MultiDex
	/================================================*/
	const { dexName } = getDexConfig(dex);

	const { step, nextStep } = useStepList()

	return (
		pitstopConfigured && showLoginPage && (
			<div className='layout-login'>
				<DexLeftComponent />
				<div className='layout-login__right'>
					<div className='login-box'>
						{
							step === 1 && (
								<>
									<div className='login-box__title'>
										Welcome to { dexName } Pitstop Portal
									</div>
									<div className='login-box__sub-title'>Please log in to continue</div>
									<div className='login-box__content'>
										<form className='login-box__form' onSubmit={handleSubmit(data => loginHandler(data))} autoComplete="off" data-testid="form">
											<div className='login-box__form__cols'>
												{errorMessage && (
													<div className='login-box__form__col--error'>
														<Error /> <span className='t--required'>{errorMessage}</span>
													</div>
												)}
												<div className='login-box__form__col'>
													<Input
														name="email"
														register={register({
															required: "This field is mandatory",
															validate: {
																checkEmail: v => handleChange(v) || "Please enter a valid email"
															}
														})}
														placeholder="Email"
														error={errors.email}
														errorMessage={errors.email?.message}
														onBlur={(e) => { e.target.value = e.target.value.trim() }}
														// onChange={(e) => { handleChange(e.target.value) }}
														autoComplete="off"
														data-testid="email"
														disabled={changeButtonTextForLockedUsers}
													/>
												</div>
												{passwordField &&
													<>
														<div className='login-box__form__col login-box__form-position'>
															<Input
																name='password'
																className={passwordInputClass}
																type={customMask ? 'password' : 'text'}
																placeholder="Password"
																register={register({
																	required: passwordField
																})}
																autoComplete="off"
																data-testid="secret-input"
																// onChange={(e) => { setPasswordFieldState(true) }}
																disabled={changeButtonTextForLockedUsers}
															/>
															<span data-testid="reveal-btn" className="link icon-eye" onClick={() => setCustomMask(!customMask)}>
																<IconEye bool={customMask} />
															</span>
														</div>
														{errors.password && (<span className='t--required'>Required</span>)}
														<div className='login-box__form__col login-box__form__col--row'>
															{(
																<div className="login-box__form__col__left loging-box__text loging-box__text--theme link">
																	<span data-testid="trouble" id="forgot-password-id" onClick={() => goToForgetPassword()}>Forgot Password?</span>
																</div>
															)}
														</div>
													</>
												}

												{!useTencentCaptcha && googleKeys[dex]?.[env]?.CAPTCHA_SITE_KEY && (
														<ReCAPTCHA sitekey={`${googleKeys[dex]?.[env]?.CAPTCHA_SITE_KEY}`} ref={captchaRef} style={{ marginTop: "20px" }} onChange={(value) => handleChangeReCaptcha(value)} data-testid="recaptcha-test" />
													)
												}
												{useTencentCaptcha && (
													<TencentCaptcha
														appId={tencentCaptchaAppId}
														onVerify={handleTencentReCaptchaChange}
														data-testid="tencent-recaptcha-test"
													/>
												)}
												<div className='login-box__form__actions-submit'>
													{!changeButtonTextForLockedUsers && <Button type='submit' text="Log In" id="login-button-trigger-id" maxWidth="168" full round loading={loading}
														disabled={
															!isValid ||
															(!useTencentCaptcha && defaultCaptchaValue && !captchaToken) ||
															(useTencentCaptcha && !tencentCaptchaToken)
														}
													/>}
													{changeButtonTextForLockedUsers && <Button text="Back to Log In" type="submit" maxWidth="168" full round />}
												</div>
											</div>
										</form>
									</div>
								</>
							)
						}
						{
							step === 2 && (
								<>
									<div className='login-box__title'>Welcome to { dexName } Pitstop Portal</div>
									<div className='login-box__sub-title'>Enter the Security Code generated in Google Authenticator App.</div>
									<div className='login-box__content'>
										<form className='login-box__form' onSubmit={handleSubmit(data => otpHandler(data))} autoComplete="off">
											<div className='login-box__form__cols'>
												{errorMessage && (
													<div className='login-box__form__col--error'>
														<Error /> <span className='t--required'>{errorMessage}</span>
													</div>
												)}
												<div className='login-box__form__col'>
													<Input
														maxWidth="360"
														name="otp"
														placeholder="000000"
														register={register({
															required: "This field is mandatory",
															minLength: {
																value: 6,
																message: "Please enter a valid OTP"
															}
														})}
														autoComplete="off"
														error={errors.otp}
														errorMessage={errors.otp?.message}
														maxLength={6}
													/>
												</div>
												<div className='login-box__form__col login-box__form__col--row'>
													{(
														<div className="login-box__form__col__left loging-box__text  loging-box__text--theme link">
															<span data-testid="trouble2" onClick={() => goToResetTfa()}>Trouble with Security Code?</span>
														</div>
													)}
												</div>
												<div className='login-box__form__actions-submit'>
													<Button text="Verify & Login" type="submit" maxWidth="240" full round disabled={!isValid} loading={loading} />
												</div>
											</div>
										</form>
									</div>
								</>
							)
						}


					</div>
				</div>
				<Toast2 show={error} errorMessage={error} close={() => { handleError() }} />
				<div className="version__no">
					v{version?.pitstopVersion}
				</div>
			</div>
		)
	)
}

export default Login
